import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getDocuments,
  getProjects,
  getDocumentDownload,
  deleteDocument,
  Document,
  Project,
} from '../services/api'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

export default function GlobalDocuments() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('all')
  const [projectFilter, setProjectFilter] = useState('all')
  const [formatFilter, setFormatFilter] = useState('all')
  const [sortBy, setSortBy] = useState('date')
  const [search, setSearch] = useState('')

  const { data: documents, isLoading: loadingDocuments } = useQuery({
    queryKey: ['documents'],
    queryFn: () => getDocuments().then((res) => res.data),
    refetchInterval: 10000,
  })

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects().then((res) => res.data),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
  })

  // Create a project lookup map
  const projectMap = useMemo(() => {
    const map: Record<string, Project> = {}
    if (projects) {
      projects.forEach((p) => { map[p.id] = p })
    }
    return map
  }, [projects])

  const handleDownload = async (doc: Document) => {
    try {
      const response = await getDocumentDownload(doc.id)
      window.open(response.data.download_url, '_blank')
    } catch (error) {
      console.error('Download failed:', error)
      alert('Failed to download document')
    }
  }

  const handleDelete = (doc: Document) => {
    if (confirm(`Delete "${doc.template_name}" document? This cannot be undone.`)) {
      deleteMutation.mutate(doc.id)
    }
  }

  // Compute stats
  const stats = useMemo(() => {
    if (!documents) return { total: 0, completed: 0, generating: 0, error: 0 }
    return {
      total: documents.length,
      completed: documents.filter((d) => d.status === 'completed').length,
      generating: documents.filter((d) => d.status === 'generating' || d.status === 'pending').length,
      error: documents.filter((d) => d.status === 'error').length,
    }
  }, [documents])

  const filteredDocuments = useMemo(() => {
    if (!documents) return []
    let filtered = [...documents]

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter((d) => {
        const matchesTemplate = d.template_name.toLowerCase().includes(searchLower)
        const project = projectMap[d.project_id]
        const matchesProject = project?.project_name.toLowerCase().includes(searchLower) ||
          project?.customer_name.toLowerCase().includes(searchLower)
        return matchesTemplate || matchesProject
      })
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((d) => d.status === statusFilter)
    }

    // Apply project filter
    if (projectFilter !== 'all') {
      filtered = filtered.filter((d) => d.project_id === projectFilter)
    }

    // Apply format filter
    if (formatFilter !== 'all') {
      filtered = filtered.filter((d) => d.output_format === formatFilter)
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.template_name.localeCompare(b.template_name)
        case 'size':
          return (b.file_size || 0) - (a.file_size || 0)
        case 'project':
          const projA = projectMap[a.project_id]?.project_name || ''
          const projB = projectMap[b.project_id]?.project_name || ''
          return projA.localeCompare(projB)
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      }
    })

    return filtered
  }, [documents, statusFilter, projectFilter, formatFilter, sortBy, search, projectMap])

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '--'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getStatusBadge = (status: Document['status']) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(52, 211, 153, 0.2)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
            <i className="fa-solid fa-check"></i>
            <span>Completed</span>
          </span>
        )
      case 'generating':
      case 'pending':
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
            <i className="fa-solid fa-spinner fa-spin"></i>
            <span>Generating</span>
          </span>
        )
      case 'error':
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(248, 113, 113, 0.2)', color: '#f87171', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
            <i className="fa-solid fa-times"></i>
            <span>Error</span>
          </span>
        )
      default:
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(148, 163, 184, 0.2)', color: 'rgba(148, 163, 184, 0.8)', border: '1px solid rgba(148, 163, 184, 0.3)' }}>
            <i className="fa-solid fa-circle text-[8px]"></i>
            <span>Pending</span>
          </span>
        )
    }
  }

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'docx':
        return <i className="fa-solid fa-file-word" style={{ color: '#60a5fa' }}></i>
      case 'pdf':
        return <i className="fa-solid fa-file-pdf" style={{ color: '#f87171' }}></i>
      default:
        return <i className="fa-solid fa-file-lines" style={{ color: '#a78bfa' }}></i>
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Documents</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>All generated documents across projects</p>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-80 pl-10 pr-4 py-2.5 rounded-xl text-sm"
            style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
          />
          <i className="fa-solid fa-search absolute left-3 top-3" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              <i className="fa-solid fa-file-lines text-xl" style={{ color: '#60a5fa' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Total Documents</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.total}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
              <i className="fa-solid fa-check-circle text-xl" style={{ color: '#34d399' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Completed</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.completed}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
              <i className="fa-solid fa-spinner text-xl" style={{ color: '#fbbf24' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Generating</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.generating}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(248, 113, 113, 0.15)', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
              <i className="fa-solid fa-exclamation-triangle text-xl" style={{ color: '#f87171' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Errors</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.error}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Filter:</span>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="all">All Projects</option>
              {projects?.map((p) => (
                <option key={p.id} value={p.id}>{p.project_name}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="generating">Generating</option>
              <option value="completed">Completed</option>
              <option value="error">Error</option>
            </select>
            <select
              value={formatFilter}
              onChange={(e) => setFormatFilter(e.target.value)}
              className="text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="all">All Formats</option>
              <option value="docx">Word (.docx)</option>
              <option value="pdf">PDF</option>
              <option value="md">Markdown</option>
            </select>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
            >
              <option value="date">Date</option>
              <option value="name">Name</option>
              <option value="size">Size</option>
              <option value="project">Project</option>
            </select>
          </div>
        </div>
      </div>

      {/* Documents List */}
      {loadingDocuments ? (
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#60a5fa' }}></i>
            <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading documents...</p>
          </div>
        </div>
      ) : filteredDocuments.length > 0 ? (
        <div className="space-y-4">
          {filteredDocuments.map((doc) => {
            const project = projectMap[doc.project_id]
            return (
              <div
                key={doc.id}
                className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.01]"
                style={{ ...glassPanel, borderColor: doc.status === 'error' ? 'rgba(248, 113, 113, 0.3)' : 'rgba(100, 180, 255, 0.1)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{
                        background: doc.status === 'error' ? 'rgba(248, 113, 113, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                        border: doc.status === 'error' ? '1px solid rgba(248, 113, 113, 0.3)' : '1px solid rgba(59, 130, 246, 0.3)'
                      }}
                    >
                      {getFormatIcon(doc.output_format)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base font-semibold mb-1" style={{ color: '#e2e8f0' }}>
                        {doc.template_name}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm flex-wrap gap-2" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                        {project && (
                          <Link
                            to={`/projects/${project.id}`}
                            className="flex items-center space-x-1 hover:text-blue-400 transition"
                          >
                            <i className="fa-solid fa-folder text-xs"></i>
                            <span>{project.project_name}</span>
                          </Link>
                        )}
                        <span className="flex items-center space-x-1">
                          <i className="fa-solid fa-file text-xs"></i>
                          <span className="uppercase">{doc.output_format}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <i className="fa-solid fa-database text-xs"></i>
                          <span>{formatFileSize(doc.file_size)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <i className="fa-solid fa-calendar text-xs"></i>
                          <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                        </span>
                        {doc.completed_at && (
                          <span className="flex items-center space-x-1">
                            <i className="fa-solid fa-clock text-xs"></i>
                            <span>Completed {new Date(doc.completed_at).toLocaleString()}</span>
                          </span>
                        )}
                      </div>
                      {doc.error_message && (
                        <p className="text-sm flex items-center space-x-1 mt-2" style={{ color: '#f87171' }}>
                          <i className="fa-solid fa-exclamation-triangle"></i>
                          <span>{doc.error_message}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {getStatusBadge(doc.status)}
                    {doc.status === 'completed' && (
                      <button
                        onClick={() => handleDownload(doc)}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center space-x-2"
                        style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
                      >
                        <i className="fa-solid fa-download"></i>
                        <span>Download</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(doc)}
                      disabled={deleteMutation.isPending}
                      className="p-2 rounded-lg transition disabled:opacity-50"
                      style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                      onMouseOver={(e) => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.background = 'rgba(248, 113, 113, 0.1)' }}
                      onMouseOut={(e) => { e.currentTarget.style.color = 'rgba(148, 163, 184, 0.6)'; e.currentTarget.style.background = 'transparent' }}
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                    <Link
                      to={`/projects/${doc.project_id}/documents`}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition"
                      style={{ background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)', color: '#60a5fa' }}
                    >
                      View in Project
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(100, 116, 139, 0.2)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
            <i className="fa-solid fa-file-lines text-3xl" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            {search || statusFilter !== 'all' || projectFilter !== 'all' || formatFilter !== 'all' ? 'No documents match your filters' : 'No documents yet'}
          </h3>
          <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
            {search || statusFilter !== 'all' || projectFilter !== 'all' || formatFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Generate documents in your projects to get started'}
          </p>
          {!search && statusFilter === 'all' && projectFilter === 'all' && formatFilter === 'all' && (
            <Link
              to="/projects"
              className="px-6 py-3 rounded-lg text-sm font-medium transition inline-flex items-center space-x-2"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
            >
              <i className="fa-solid fa-folder"></i>
              <span>Go to Projects</span>
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
